/**
 * Cambiar mi propia contrasena, con la sesion abierta.
 *
 * Pide la actual a proposito: sin eso, una sesion olvidada en la computadora de
 * la administracion alcanza para quedarse con la cuenta para siempre.
 *
 * Al guardar, el backend corta las sesiones y los JWT anteriores — INCLUIDO el
 * de esta pestana. La pantalla no pelea contra eso: avisa, cierra la sesion y
 * manda al ingreso. Fingir que la sesion sigue viva llevaria a la persona a un
 * 401 en la pantalla siguiente, sin entender por que.
 */

import { useEffect, useState, type FormEvent } from 'react';

import { PageHeader } from '@/components/shell/primitives';
import { Button } from '@/components/shell/primitives';
import { useSessionStore } from '@/store/session';
import { AccesoError, cambiarMiContrasena } from '@/views/acceso/api';
import { Aviso, CampoContrasena } from '@/views/acceso/piezas';

/** Segundos que se muestra el cartel de éxito antes de volver al ingreso. */
const ESPERA_ANTES_DE_SALIR = 4;

export function CambiarClaveScreen() {
  const usuario = useSessionStore((estado) => estado.user);
  const signOut = useSessionStore((estado) => estado.signOut);

  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repeticion, setRepeticion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);
  const [cuenta, setCuenta] = useState(ESPERA_ANTES_DE_SALIR);

  // La cuenta regresiva existe para que el cartel se llegue a leer. Si se saliera
  // al instante, la persona veria un parpadeo y la pantalla de ingreso, y no
  // sabria si el cambio se guardo.
  useEffect(() => {
    if (!listo) return;
    if (cuenta <= 0) {
      signOut();
      return;
    }
    const id = window.setTimeout(() => {
      setCuenta((previo) => previo - 1);
    }, 1000);
    return () => {
      window.clearTimeout(id);
    };
  }, [listo, cuenta, signOut]);

  async function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    setError(null);
    if (nueva !== repeticion) {
      setError('Las dos contraseñas no coinciden.');
      return;
    }
    setEnviando(true);
    try {
      await cambiarMiContrasena(actual, nueva, repeticion);
      setListo(true);
    } catch (fallo) {
      setError(fallo instanceof AccesoError ? fallo.message : 'No se pudo cambiar la contraseña.');
    } finally {
      setEnviando(false);
    }
  }

  if (listo) {
    return (
      <div className="mx-auto max-w-lg px-4 py-8 sm:px-6">
        <Aviso tono="exito">
          <p className="font-medium">Tu contraseña cambió.</p>
          <p className="mt-1">
            Por seguridad se cerraron todas las sesiones abiertas con la anterior, incluida ésta. Te
            llevamos al ingreso en {cuenta}…
          </p>
        </Aviso>
        <Button
          variant="primary"
          className="mt-4"
          onClick={() => {
            signOut();
          }}
        >
          Ir al ingreso ahora
        </Button>
      </div>
    );
  }

  const puedeEnviar = actual !== '' && nueva !== '' && repeticion !== '' && !enviando;

  return (
    <div className="mx-auto max-w-lg px-4 py-8 sm:px-6">
      <PageHeader
        title="Cambiar mi contraseña"
        {...(usuario === null ? {} : { description: `Cuenta: ${usuario.email}` })}
      />

      {error !== null && (
        <div className="mt-5">
          <Aviso tono="error">{error}</Aviso>
        </div>
      )}

      <form
        onSubmit={(evento) => {
          void enviar(evento);
        }}
        className="mt-5 space-y-4 rounded-xl border border-line bg-surface p-5 shadow-sm"
      >
        <input
          type="text"
          name="email"
          autoComplete="username"
          value={usuario?.email ?? ''}
          readOnly
          hidden
        />

        <CampoContrasena
          id="clave-actual"
          label="Contraseña actual"
          valor={actual}
          onChange={setActual}
          autoComplete="current-password"
          autoFocus
        />

        <hr className="border-line" />

        <CampoContrasena
          id="clave-nueva"
          label="Contraseña nueva"
          valor={nueva}
          onChange={setNueva}
          autoComplete="new-password"
          mostrarFuerza
          email={usuario?.email ?? ''}
          nombre={usuario?.name ?? ''}
        />
        <CampoContrasena
          id="clave-nueva-repetida"
          label="Repetila"
          valor={repeticion}
          onChange={setRepeticion}
          autoComplete="new-password"
        />

        {repeticion !== '' && nueva !== repeticion && (
          <p className="text-xs text-danger">Las dos contraseñas no coinciden.</p>
        )}

        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-xs text-fg-muted">
            Al guardar se cierran las sesiones abiertas, incluida ésta.
          </p>
          <Button type="submit" variant="primary" loading={enviando} disabled={!puedeEnviar}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </form>
    </div>
  );
}
